import { numFont } from '../../../utils/fonts';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import axios from 'axios';
import { FOCUS_RING_SX, IconTile, Overline } from './builderUi';
import { useUserStore } from '../../../store';
import { usePartCodeOnly } from '../../../utils/partSearchPrefs';
import { formatPrice } from '../../../utils/vat';
import useOrderableSearch from './useOrderableSearch';
import SearchResultsPane from './SearchResultsPane';
import FreeTextLineForm from './FreeTextLineForm';
import OrderCart from './OrderCart';
import ReviewAndSendDialog from './ReviewAndSendDialog';
import {
  buildCreatePayload,
  cartGroups,
  cartTotals,
  draftStorageKey,
  freeTextLine,
  lineFromRow,
} from './cartUtils';

const DRAFT_VERSION = 1;

function readDraft(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== DRAFT_VERSION || !Array.isArray(parsed.lines)) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function writeDraft(key, draft) {
  try {
    if (!draft.lines.length) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify({ version: DRAFT_VERSION, ...draft }));
  } catch (e) {
    /* storage full or blocked: the draft is a convenience */
  }
}

/** The row a cart line came from, so the result list can show "Korissa 2". */
function rowKeyOfLine(line) {
  return line.rowKey || null;
}

/**
 * Uusi tilaus: build a supplier order from any orderable source (register, vendor
 * catalogues, the vendors' own APIs), give every line a destination, and send one
 * order per vendor. Page, not dialog: the cart survives a refresh and the URL can
 * preset a destination (?maintenance=<id>, ?parts_sale=<id>).
 */
function SupplierOrderBuilderView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const { currentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;
  const storageKey = draftStorageKey(orgId);

  const [context, setContext] = useState(null);
  const [contextError, setContextError] = useState(false);
  const [codeOnly, setCodeOnly] = usePartCodeOnly();
  const [input, setInput] = useState('');
  const [lines, setLines] = useState([]);
  const [defaultDestination, setDefaultDestination] = useState({ kind: 'stock' });
  const [newLahete, setNewLahete] = useState(null);
  const [freeTextOpen, setFreeTextOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [orderOptions, setOrderOptions] = useState({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [lineErrors, setLineErrors] = useState({});
  const [snack, setSnack] = useState(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const restoredRef = useRef(false);

  const searchState = useOrderableSearch({ context, codeOnly });
  const canViewPrices = !!context?.can_view_purchase_prices;
  const emailIntegrations = useMemo(() => (context?.integrations || []).filter((i) => i.channel === 'email'), [context]);

  // Builder context: orderable channels + destination gates.
  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/supplier-orders/builder-context/')
      .then((resp) => {
        if (!cancelled) setContext(resp.data);
      })
      .catch(() => {
        if (!cancelled) setContextError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Draft restore (once per org), then the URL presets.
  useEffect(() => {
    if (!orgId || restoredRef.current) return;
    restoredRef.current = true;
    const draft = readDraft(storageKey);
    if (draft) {
      setLines(draft.lines);
      if (draft.defaultDestination) setDefaultDestination(draft.defaultDestination);
      if (draft.newLahete) setNewLahete(draft.newLahete);
      setSnack({ severity: 'info', message: t('orderBuilder.draftRestored') });
    }
    const maintenanceId = searchParams.get('maintenance');
    const partsSaleId = searchParams.get('parts_sale');
    if (maintenanceId) {
      axios
        .get(`/api/maintenances/${maintenanceId}/`)
        .then((resp) => {
          const m = resp.data;
          setDefaultDestination({
            kind: 'maintenance',
            maintenance_id: m.id,
            maintenance: {
              id: m.id,
              work_number: m.work_number,
              registration_number: m.vehicle?.registration_number || m.vehicle_registration || '',
              vehicle_name: m.vehicle ? `${m.vehicle.manufacturer || ''} ${m.vehicle.model || ''}`.trim() : '',
              customer_name: m.customer_name || '',
            },
          });
        })
        .catch(() => {});
    } else if (partsSaleId) {
      axios
        .get(`/api/parts-sales/${partsSaleId}/`)
        .then((resp) => {
          const s = resp.data;
          setDefaultDestination({ kind: 'parts_sale', parts_sale_id: s.id, parts_sale: { id: s.id, number: s.number, customer_name: s.customer_name } });
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Draft autosave.
  useEffect(() => {
    if (!orgId || !restoredRef.current) return;
    writeDraft(storageKey, { lines, defaultDestination, newLahete });
  }, [lines, defaultDestination, newLahete, storageKey, orgId]);

  const cartByRow = useMemo(() => {
    const map = {};
    lines.forEach((line) => {
      const key = rowKeyOfLine(line);
      if (key) map[key] = line;
    });
    return map;
  }, [lines]);

  const updateLine = useCallback((key, patch) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
    setLineErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const removeLine = useCallback((key) => {
    setLines((prev) => prev.filter((line) => line.key !== key));
  }, []);

  const addRow = useCallback(
    async (row) => {
      let verifiedHit = null;
      if (row.verifyOnAdd && row.integration) {
        verifiedHit = await searchState.verifyCode(row.integration.id, row.code);
        if (!verifiedHit) return; // the row now shows why (unknown / vendor did not answer)
      }
      const line = { ...lineFromRow(row, { destination: defaultDestination, verifiedHit }), rowKey: row.rowKey };
      setLines((prev) => [...prev, line]);
    },
    [defaultDestination, searchState]
  );

  const changeRowQuantity = useCallback(
    (row, delta) => {
      const line = cartByRow[row.rowKey];
      if (!line) return;
      const next = (parseFloat(line.quantity) || 0) + delta;
      if (next <= 0) removeLine(line.key);
      else updateLine(line.key, { quantity: String(next) });
    },
    [cartByRow, removeLine, updateLine]
  );

  const addFreeText = useCallback(
    (values) => {
      setLines((prev) => [...prev, freeTextLine({ ...values, destination: defaultDestination })]);
      setFreeTextOpen(false);
    },
    [defaultDestination]
  );

  const clearCart = () => {
    setLines([]);
    setLineErrors({});
    setClearConfirmOpen(false);
  };

  const groups = useMemo(() => cartGroups(lines, context), [lines, context]);
  const totals = useMemo(() => cartTotals(lines), [lines]);

  const send = async () => {
    setSending(true);
    setSendError(null);
    setLineErrors({});
    try {
      const payload = buildCreatePayload({ lines, groups, newLahete, orderOptions });
      const resp = await axios.post('/api/supplier-orders/create-from-cart/', payload);
      const data = resp.data;
      setResult(data);
      const placedKeys = new Set();
      (data.orders || []).forEach((o) => {
        if (o.status === 'placed') (o.line_keys || []).forEach((k) => placedKeys.add(k));
      });
      setLines((prev) =>
        prev
          .filter((line) => !placedKeys.has(line.key))
          .map((line) =>
            // The new lähete survived in a placed group: later retries add to it, not to a second one.
            data.created_parts_sale && line.destination?.kind === 'new_parts_sale'
              ? {
                  ...line,
                  destination: {
                    kind: 'parts_sale',
                    parts_sale_id: data.created_parts_sale.id,
                    parts_sale: { id: data.created_parts_sale.id, number: data.created_parts_sale.number, customer_name: newLahete?.customer?.name || '' },
                  },
                }
              : line
          )
      );
    } catch (err) {
      const data = err.response?.data;
      const status = err.response?.status;
      if (!err.response) {
        setSendError(t('purchaseOrders.batchOrder.connectionError'));
      } else if (status === 400 && Array.isArray(data?.line_errors)) {
        const errors = {};
        data.line_errors.forEach((e) => {
          errors[e.key] = e.expected
            ? t(`orderBuilder.lineError.${e.code}`, { vendor: e.expected.display_name })
            : t(`orderBuilder.lineError.${e.code}`);
        });
        setLineErrors(errors);
        setSendError(t('orderBuilder.sendErrors.validation'));
        setReviewOpen(false);
      } else if (Array.isArray(data?.line_keys) && data.line_keys.length) {
        const errors = {};
        data.line_keys.forEach((k) => {
          errors[k] = data.code === 'unrecognised_codes' ? t('orderBuilder.lineError.unrecognised') : data.detail || data.error;
        });
        setLineErrors(errors);
        setSendError(data.error || data.detail || t('purchaseOrders.batchOrder.orderFailed'));
        setReviewOpen(false);
      } else {
        setSendError(data?.error || data?.detail || t('purchaseOrders.batchOrder.orderFailed'));
      }
    } finally {
      setSending(false);
    }
  };

  const closeReview = () => {
    setReviewOpen(false);
    setResult(null);
    setSendError(null);
  };

  const retryFailed = () => {
    setResult(null);
    setSendError(null);
  };

  const done = () => {
    const firstPlaced = (result?.orders || []).find((o) => o.status === 'placed');
    setResult(null);
    setReviewOpen(false);
    if (lines.length === 0) {
      writeDraft(storageKey, { lines: [] });
      navigate(firstPlaced?.order?.id ? `/supplier-orders?order=${firstPlaced.order.id}` : '/supplier-orders');
    } else {
      setSnack({ severity: 'warning', message: t('orderBuilder.result.failedKept') });
    }
  };

  // ---- rendering --------------------------------------------------------------

  if (contextError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('orderBuilder.contextFailed')}</Alert>
      </Box>
    );
  }
  if (!context) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const noChannels = (context.integrations || []).length === 0;

  // The page is one white card on the workspace, laid out like Tarjousautomaatti: a
  // heading strip with the navy icon tile, search on the left, the cart on a near-white
  // panel to the right.
  const shellSx = {
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '16px',
    overflow: 'hidden',
    ...FOCUS_RING_SX,
  };

  const header = (
    <Box
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: { xs: 1.25, md: 1.75 }, px: { xs: 1.5, md: 3 }, py: { xs: 1.75, md: 2.25 },
        borderBottom: '1px solid', borderColor: 'divider',
      }}
    >
      <IconButton onClick={() => navigate('/supplier-orders')} aria-label={t('Back')} data-testid="builder-back" sx={{ color: 'text.secondary', mt: 0.25, ml: -0.5 }}>
        <ArrowBackIcon />
      </IconButton>
      <IconTile sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
        <AddShoppingCartIcon />
      </IconTile>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="h1" sx={{ fontSize: { xs: 17, md: 19 }, fontWeight: 700, letterSpacing: '-.3px', lineHeight: 1.2 }}>
          {t('orderBuilder.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.375, lineHeight: 1.4 }}>
          {t('orderBuilder.subtitle')}
        </Typography>
      </Box>
    </Box>
  );

  if (noChannels) {
    return (
      <Box data-testid="order-builder-page" sx={shellSx}>
        {header}
        <Box sx={{ px: 3, py: { xs: 4, md: 6 }, textAlign: 'center' }} data-testid="builder-no-channels">
          <Box
            sx={{
              width: 52, height: 52, borderRadius: '14px', bgcolor: 'background.default', border: '1px solid', borderColor: 'divider',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', mb: 1.5,
            }}
          >
            <LinkOffIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 0.5 }}>{t('orderBuilder.noChannels.title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.45, maxWidth: 520, mx: 'auto' }}>
            {t('orderBuilder.noChannels.help')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button component={RouterLink} to="/settings?tab=integrations" variant="outlined" sx={{ textTransform: 'none', fontWeight: 600 }}>
              {t('orderBuilder.noChannels.integrations')}
            </Button>
            <Button component={RouterLink} to="/vendors" variant="outlined" sx={{ textTransform: 'none', fontWeight: 600 }}>
              {t('orderBuilder.noChannels.vendors')}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  const searchBox = (
    <Box>
      <TextField
        fullWidth
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          searchState.search(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') searchState.search(input, { immediate: true });
        }}
        placeholder={t('orderBuilder.searchPlaceholder')}
        autoFocus={!isMobile}
        inputProps={{ 'data-testid': 'builder-search-input', style: { fontSize: 15.5, fontWeight: 500 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: input ? (
            <InputAdornment position="end">
              {searchState.anyLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
              <IconButton
                size="small"
                onClick={() => {
                  setInput('');
                  searchState.search('', { immediate: true });
                }}
                aria-label={t('orderBuilder.clearSearch')}
                sx={{ color: 'text.secondary' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
          sx: { borderRadius: '12px', bgcolor: 'background.paper', '& .MuiOutlinedInput-input': { py: 1.5 } },
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
        <FormControlLabel
          sx={{ ml: -0.75, mr: 0 }}
          control={<Checkbox size="small" checked={codeOnly} onChange={(e) => setCodeOnly(e.target.checked)} sx={{ py: 0.5 }} />}
          label={<Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>{t('orderBuilder.codeOnly')}</Typography>}
        />
        {emailIntegrations.length > 0 && (
          <Button
            size="small"
            startIcon={<PlaylistAddIcon />}
            onClick={() => setFreeTextOpen((v) => !v)}
            sx={{ textTransform: 'none', fontWeight: 600, ml: 'auto', px: 0.75 }}
            data-testid="free-text-toggle"
          >
            {t('orderBuilder.freeText.button')}
          </Button>
        )}
      </Box>
      {freeTextOpen && (
        <FreeTextLineForm emailIntegrations={emailIntegrations} onAdd={addFreeText} onCancel={() => setFreeTextOpen(false)} canViewPrices={canViewPrices} />
      )}
      {sendError && (
        <Alert severity="error" sx={{ mt: 1.5, borderRadius: '12px' }} onClose={() => setSendError(null)} data-testid="builder-send-error">
          {sendError}
        </Alert>
      )}
    </Box>
  );

  const cart = (
    <OrderCart
      context={context}
      lines={lines}
      onUpdateLine={updateLine}
      onRemoveLine={removeLine}
      onClear={() => setClearConfirmOpen(true)}
      defaultDestination={defaultDestination}
      onDefaultDestinationChange={setDefaultDestination}
      newLahete={newLahete}
      onNewLaheteChange={setNewLahete}
      onContinue={() => {
        setSendError(null);
        setReviewOpen(true);
      }}
      canViewPrices={canViewPrices}
      lineErrors={lineErrors}
      dense={isMobile}
    />
  );

  const results = (
    <SearchResultsPane
      searchState={searchState}
      cartByRow={cartByRow}
      onAdd={addRow}
      onChangeQuantity={changeRowQuantity}
      canViewPrices={canViewPrices}
      onRetryVerify={(row) => searchState.clearVerification(row.integration.id, row.code)}
    />
  );

  const cartHeading = (
    <Box sx={{ px: { xs: 1.5, md: 2 }, pt: { xs: 1.75, md: 2.25 }, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
      <ShoppingCartOutlinedIcon sx={{ fontSize: 20, color: lines.length ? 'primary.main' : 'text.disabled' }} />
      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{t('orderBuilder.cartTitle')}</Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ml: 'auto', ...numFont }}>
        {t('orderBuilder.cartBar', { rows: lines.length, vendors: groups.length })}
      </Typography>
    </Box>
  );

  return (
    <Box data-testid="order-builder-page" sx={{ ...shellSx, mb: isMobile ? 10 : 0 }}>
      {header}
      {isMobile ? (
        <>
          <Box sx={{ px: 1.5, pt: 2, pb: 3, bgcolor: 'background.default' }}>
            {searchBox}
            <Box sx={{ mt: 2 }}>{results}</Box>
          </Box>
          <Paper
            elevation={0}
            square
            sx={{
              position: 'fixed', left: 0, right: 0, bottom: 0, px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5,
              zIndex: (th) => th.zIndex.appBar, borderTop: '1px solid', borderColor: 'divider', boxShadow: '0 -6px 20px rgba(15, 35, 64, 0.08)',
            }}
            data-testid="cart-bottom-bar"
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Overline sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t('orderBuilder.cartBar', { rows: lines.length, vendors: groups.length })}
              </Overline>
              {canViewPrices && lines.length > 0 ? (
                <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.5px', lineHeight: 1.15, ...numFont }} noWrap>
                  {formatPrice(totals.total)} €{' '}
                  <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary', letterSpacing: 0 }}>{t('orderBuilder.exVatTag')}</Box>
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.disabled' }} noWrap>
                  {t('orderBuilder.cartTitle')}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              onClick={() => setCartOpen(true)}
              startIcon={<ShoppingCartIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', flexShrink: 0 }}
              data-testid="cart-open"
            >
              {t('orderBuilder.showCart')}
            </Button>
          </Paper>
          <Dialog open={cartOpen} onClose={() => setCartOpen(false)} fullScreen data-testid="cart-panel" sx={FOCUS_RING_SX}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <IconTile size={32}>
                <ShoppingCartOutlinedIcon />
              </IconTile>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 17, letterSpacing: '-.2px', lineHeight: 1.2 }}>{t('orderBuilder.cartTitle')}</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ...numFont }} noWrap>
                  {t('orderBuilder.cartBar', { rows: lines.length, vendors: groups.length })}
                </Typography>
              </Box>
              <IconButton onClick={() => setCartOpen(false)} aria-label={t('Close')} data-testid="cart-close" sx={{ color: 'text.secondary', mr: -1 }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, bgcolor: 'background.default' }}>{cart}</DialogContent>
          </Dialog>
        </>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)' }}>
          <Box sx={{ px: 3, py: 3, minWidth: 0 }}>
            {searchBox}
            <Box sx={{ mt: 2.5 }}>{results}</Box>
          </Box>
          <Box sx={{ bgcolor: 'background.default', borderLeft: '1px solid', borderColor: 'divider', minWidth: 0 }}>
            <Box sx={{ position: 'sticky', top: 16, maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column' }}>
              {cartHeading}
              {cart}
            </Box>
          </Box>
        </Box>
      )}

      <ReviewAndSendDialog
        open={reviewOpen}
        onClose={closeReview}
        context={context}
        groups={groups}
        newLahete={newLahete}
        orderOptions={orderOptions}
        onOrderOptionsChange={setOrderOptions}
        onSend={send}
        sending={sending}
        result={result}
        onRetryFailed={retryFailed}
        onDone={done}
        canViewPrices={canViewPrices}
        error={sendError}
      />

      <Dialog open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.2px', pb: 0.5 }}>{t('orderBuilder.clearCart')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{t('orderBuilder.clearCartConfirm', { count: lines.length })}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button onClick={() => setClearConfirmOpen(false)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            {t('Cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={clearCart} data-testid="cart-clear-confirm" sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>
            {t('orderBuilder.clearCart')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        {snack ? (
          <Alert onClose={() => setSnack(null)} severity={snack.severity} sx={{ width: '100%' }}>
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>

    </Box>
  );
}

export default SupplierOrderBuilderView;
